<template>
  <div
    :class="classes"
    data-sb-regression="sb-field"
  >
    <!--
      @slot slot to show the field contents
    -->
    <slot
      v-if="usingScoped"
      :valid="valid"
      :message="message"
      :show-validation="showValidation"
    />
    <slot v-else />
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import uuid from '@src/utils/uuid';
import sbFieldModel from '@src/utils/models/sbFieldModel';

/**
 * This is a wrapper component to be used with labels, inputs, and helper-text.
 * This component is used to apply a uniform style across all of these components.
 * For eg. if your field has an error because the input has an incorrect value, you can apply the `error` variation to the field.
 * This would cause all of the contained elements to be styled in the error styles.
 */
export default {
  name: 'SbField',
  inject: {
    sbForm: {
      default: null,
    },
    sbValidator: {
      default: null,
    },
    onChange: {
      default: null,
    },
  },
  provide() {
    return {
      sbField: this.sbField,
    };
  },
  props: {
    /**
     * Style variation to give additional meaning.
     * `default|warning|error|valid`
     */
    variation: {
      type: String,
      default: 'default',
      validator: (value) => {
        return value.match(/(default|warning|error|valid)/);
      },
    },
    /**
     * Name of the field. Use this when using the `sb-form` component with validations.
     */
    name: {
      type: String,
      default: null,
      required: false,
    },
    /**
     * Automatically apply error variation. Enable this if you are using this field as part of a form with SbValidator.
     */
    autoApplyErrorState: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    sbField: cloneDeep(sbFieldModel),
    valid: null,
    showValidation: null,
    message: null,
    internalVariation: null,
    validationsWatcherUnsubscribe: null,
  }),
  computed: {
    classes() {
      return [
        'sb-form-field',
        this.internalVariation,
      ];
    },
    usingScoped() {
      return this.$slots.default && this.$slots.default();
    },
    fieldName() {
      return this.name || uuid();
    },
  },
  watch: {
    variation: {
      immediate: true,
      handler() {
        this.internalVariation = this.variation;
      },
    },
  },
  mounted() {
    if (this.sbForm) {
      this.setFormField(this.sbField);
      this.$watch(
        'sbField',
        (newField) => {
          this.setFormField(newField);
          if (typeof this.onChange === 'function' && newField.state === 'input') {
            this.onChange({
              name: this.fieldName,
              value: newField.value,
            });
          }
        },
        { deep: true },
      );
    }
    if (this.sbValidator) {
      this.validationsWatcherUnsubscribe = this.$watch(
        `sbValidator.validations.${this.fieldName}`,
        (validation) => {
          if (validation === undefined) {
            this.validationsWatcherUnsubscribe();
            return;
          }
          this.valid = validation.valid;
          this.message = validation.message;
          this.showValidation = validation.showValidation && !this.valid;
          if (this.autoApplyErrorState) this.internalVariation = this.showValidation ? 'error' : 'default';
        },
        { deep: true },
      );
    }
  },
  methods: {
    setFormField(field) {
      // Can only use this method when this is in a form.
      this.sbForm.fields[this.fieldName] = field;
    },
  },
};
</script>

<style lang="scss">
@import '@src/components/Field/style/sb-field.scss';
</style>
